(function (scope) {
  'use strict';
  const tierOption = 'Adet Kademesi';
  function options(product) {
    return product.options.map(option => typeof option === 'string' ? option : option.name);
  }
  function tierIndex(product) { return options(product).indexOf(tierOption); }
  function threshold(variant, index) {
    const value = variant.options[index];
    return /^\d+\+$/.test(String(value)) ? Number.parseInt(value, 10) : NaN;
  }
  function group(product, variant) {
    const index = tierIndex(product);
    return variant.options.filter((value, i) => i !== index).join('\u001f');
  }
  function select(product, selected, quantity) {
    const index = tierIndex(product);
    if (index < 0) return selected;
    const candidates = product.variants.filter(variant => group(product, variant) === group(product, selected));
    return candidates.filter(variant => threshold(variant, index) <= quantity)
      .sort((a, b) => threshold(b, index) - threshold(a, index))[0] || null;
  }
  function minimum(product, selected) {
    const index = tierIndex(product);
    return Math.min(...product.variants.filter(variant => group(product, variant) === group(product, selected))
      .map(variant => threshold(variant, index)).filter(Number.isFinite));
  }
  const core = { tierIndex, threshold, group, select, minimum };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (!scope.document) return;
  const root = scope.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  let busy = 0;
  async function request(path, payload) {
    const response = await fetch(root + path, payload === undefined ? {cache:'no-store'} : {
      method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || JSON.stringify(data.errors));
    return data;
  }
  function locked() {
    scope.document.documentElement.dataset.otimsanCartBusy = busy > 0 ? 'true' : 'false';
    scope.document.querySelectorAll('button[name="checkout"],input[name="checkout"]').forEach(button => {
      if (busy > 0 && !button.disabled) { button.dataset.otimsanLocked = 'true'; button.disabled = true; }
      else if (!busy && button.dataset.otimsanLocked) { button.disabled = false; delete button.dataset.otimsanLocked; }
    });
  }
  function serial(task) {
    busy++; locked();
    const result = queue.then(task);
    queue = result.catch(() => {});
    return result.finally(() => { busy--; locked(); });
  }
  async function loadProduct(handle) { return request('products/' + encodeURIComponent(handle) + '.js'); }
  function variantFor(product, line) { return product.variants.find(variant => String(variant.id) === String(line.variant_id)); }
  function pricingProperties(properties) {
    const result = {...(properties || {})};
    if (!result._otimsan_pricing_batch) {
      result._otimsan_pricing_batch = scope.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);
    }
    return result;
  }
  async function normalize(cart) {
    const products = new Map();
    for (const line of cart.items) {
      const handle = line.handle || line.url.split('/products/')[1]?.split('?')[0];
      if (!handle) continue;
      if (!products.has(handle)) products.set(handle, await loadProduct(handle));
    }
    let changed = false;
    for (const product of products.values()) {
      if (tierIndex(product) < 0) continue;
      const lines = cart.items.filter(line => String(line.product_id) === String(product.id))
        .filter(line => { const variant = variantFor(product, line); return variant && Number.isFinite(threshold(variant, tierIndex(product))); });
      for (const line of lines) {
        const variant = variantFor(product, line);
        const target = select(product, variant, line.quantity);
        if (!target || !target.available) throw new Error('Selected quantity is unavailable');
        if (String(target.id) === String(variant.id)) continue;
        const payload = {id:target.id, quantity:line.quantity, properties:pricingProperties(line.properties)};
        if (line.selling_plan_allocation) payload.selling_plan = line.selling_plan_allocation.selling_plan.id;
        const added = await request('cart/add.js', payload);
        try { cart = await request('cart/change.js', {id:line.key, quantity:0}); }
        catch (error) {
          await request('cart/change.js', {id:added.key, quantity:Math.max(0, added.quantity - line.quantity)});
          throw error;
        }
        changed = true;
      }
    }
    return {cart, changed};
  }
  async function sections(cart, names) {
    if (!names?.length) return cart;
    return request('cart/update.js', {updates:{}, sections:names, sections_url:scope.location.pathname});
  }
  function quantityTotal(cart, product, selected) {
    return cart.items.filter(line => String(line.product_id) === String(product.id))
      .filter(line => { const variant = variantFor(product, line); return variant && Number.isFinite(threshold(variant, tierIndex(product))) && group(product, variant) === group(product, selected); })
      .reduce((sum, line) => sum + line.quantity, 0);
  }
  const api = scope.OtimsanTiers = {
    ...core, loadProduct, request, quantityTotal, serial,
    changeQuantity(line, quantity, names) {
      return serial(async () => {
        const before = await request('cart.js');
        const item = before.items[Number(line) - 1];
        if (!item) throw new Error('Cart item no longer exists');
        const handle = item.handle || item.url.split('/products/')[1]?.split('?')[0];
        const product = await loadProduct(handle);
        const selected = variantFor(product, item);
        if (quantity > 0 && tierIndex(product) >= 0 && Number.isFinite(threshold(selected, tierIndex(product)))) quantity = Math.max(quantity, minimum(product, selected));
        const updated = await request('cart/change.js', {id:item.key, quantity});
        const result = await normalize(updated);
        return sections(result.cart, names);
      });
    },
    add(product, selected, count, properties, names) {
      return serial(async () => {
        product = await loadProduct(product.handle);
        selected = product.variants.find(variant => String(variant.id) === String(selected.id));
        if (!selected) throw new Error('Variant no longer exists');
        const target = select(product, selected, count);
        if (!target?.available) throw new Error('Selected quantity is unavailable');
        await request('cart/add.js', {id:target.id, quantity:count, properties:pricingProperties(properties)});
        const result = await normalize(await request('cart.js'));
        return sections(result.cart, names);
      });
    },
    reconcile(names) {
      return serial(async () => {
        const result = await normalize(await request('cart.js'));
        return sections(result.cart, names);
      });
    }
  };
  function pendingInput() {
    return [...scope.document.querySelectorAll('input[data-otimsan-native-tier]')].some(input => input.value !== input.getAttribute('value'));
  }
  scope.document.addEventListener('click', event => {
    if ((busy || pendingInput()) && event.target.closest('a[href*="/checkout"],button[name="checkout"],input[name="checkout"]')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  scope.document.addEventListener('submit', event => {
    if ((busy || pendingInput()) && (event.submitter?.name === 'checkout' || event.target.action?.includes('/checkout'))) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
})(typeof window === 'undefined' ? globalThis : window);

